import axios from "axios";
import { computed, reactive } from "vue";
import { recordRequestError } from "./requestDiagnostics";

const baseURL = process.env.VUE_APP_BASEURL || "/api";

export const networkState = reactive({
  retrying: 0,
  pending: 0,
});

export const isNetworkRetrying = computed(() => networkState.retrying > 0);
export const isNetworkPending = computed(() => networkState.pending > 0);

export function startNetworkRequest() {
  networkState.pending += 1;
}

export function finishNetworkRequest() {
  networkState.pending = Math.max(0, networkState.pending - 1);
}

export function startNetworkRetry() {
  networkState.retrying += 1;
}

export function finishNetworkRetry() {
  networkState.retrying = Math.max(0, networkState.retrying - 1);
}

const api = axios.create({
  baseURL,
  timeout: 20000,
});

api.interceptors.request.use(
  (config) => {
    startNetworkRequest();
    config._tracksGlobalLoading = true;
    return config;
  },
  (error) => Promise.reject(error),
);

/* -------------------------------- */
/*  CONFIG RETRY INTELIGENTE         */
/* -------------------------------- */

const RETRY_DELAY = 1500;
const BACKOFF_FACTOR = 1.5;
const MAX_RETRY_DELAY = 10000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetry(error) {
  if (axios.isCancel(error)) return false;
  if (!["get", "head"].includes((error.config?.method || "get").toLowerCase())) return false;
  if (!error.response) return true;
  const status = error.response.status;
  return [502, 503, 504].includes(status);
}

export function normalizeRequestError(error) {
  const status = error.response?.status;
  if (!axios.isCancel(error) && (!error.response || status >= 500)) {
    const message = "No pudimos conectarnos. Intentá nuevamente en unos segundos.";
    error.message = message;
    if (error.response) error.response.data = { message };
  }
  return error;
}

api.interceptors.response.use(
  (response) => {
    if (response.config?._tracksGlobalLoading) {
      response.config._tracksGlobalLoading = false;
      finishNetworkRequest();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (!axios.isCancel(error)) recordRequestError({
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status || 0,
      code: error.code || "HTTP_ERROR",
      retry: originalRequest?._retryCount || 0,
    });
    if (originalRequest?._tracksGlobalLoading) {
      originalRequest._tracksGlobalLoading = false;
      finishNetworkRequest();
    }
    if (!originalRequest) {
      return Promise.reject(normalizeRequestError(error));
    }
    originalRequest._retryCount = originalRequest._retryCount || 0;

    if (shouldRetry(error)) {
      originalRequest._retryCount++;
      const delay = Math.min(
        RETRY_DELAY * Math.pow(BACKOFF_FACTOR, originalRequest._retryCount),
        MAX_RETRY_DELAY,
      );
      startNetworkRetry();
      try {
        await sleep(delay);
        return await api(originalRequest);
      } finally {
        finishNetworkRetry();
      }
    }
    return Promise.reject(normalizeRequestError(error));
  },
);

export default api;
